import { useEffect, useState } from "react";
import { Sun, TrendingDown, Leaf, AlertCircle, Loader2, RefreshCw, Home, Zap, ChevronDown } from "lucide-react";
import api from "@/lib/api";

const fmtEur = (v) => v != null ? `${Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : "—";
const fmtKwh = (v) => v != null ? `${Number(v).toLocaleString("de-DE")} kWh` : "—";

export default function PvRecommendationCard({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verdictOpen, setVerdictOpen] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/projects/${projectId}/pv-recommendation`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Berechnung nicht möglich");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [projectId]);

  if (loading) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-6 flex items-center justify-center text-zinc-500 gap-2">
        <Loader2 className="animate-spin" size={16} /> PV-Empfehlung wird berechnet…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#18181B] border border-amber-500/30 rounded-2xl p-5" data-testid="pv-recommendation-error">
        <div className="flex items-center gap-2 text-amber-300 mb-1">
          <AlertCircle size={16} /> <span className="font-semibold text-sm">PV-Empfehlung nicht verfügbar</span>
        </div>
        <p className="text-xs text-zinc-300 font-medium">{error}. Bitte zuerst den Jahresverbrauch im Verbrauchs-Tab eintragen.</p>
        <button onClick={load} className="mt-3 text-xs text-[#00E575] hover:underline inline-flex items-center gap-1">
          <RefreshCw size={11} /> Erneut versuchen
        </button>
      </div>
    );
  }

  if (!data) return null;
  const pv = data.pv || {};
  const insul = data.insulation_alternatives || [];
  const KIND_LABEL = {
    fassade: "Fassadendämmung",
    fenster: "Fensteraustausch",
    dach:    "Dachdämmung",
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 sm:p-6 space-y-5" data-testid="pv-recommendation">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Sun size={18} className="text-amber-400" /> PV-Empfehlung statt Dämmung
          </h2>
          <p className="text-xs text-zinc-400 mt-1">Automatisch berechnet aus dem Jahresverbrauch. Vergleicht PV-Kopplung mit klassischen Dämmungsmaßnahmen.</p>
        </div>
        <button onClick={load} className="text-zinc-500 hover:text-zinc-200 p-1" title="Neu berechnen" data-testid="pv-reload">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-[#0E0E12] border border-zinc-800 rounded-xl p-3">
          <div className="text-[12px] font-mono uppercase tracking-wider text-zinc-300">Heizenergie</div>
          <div className="text-base font-bold mt-1">{fmtKwh(data.annual_heating_kwh)}</div>
          <div className="text-[12px] text-zinc-300">aktuelle Anlage</div>
        </div>
        <div className="bg-[#0E0E12] border border-zinc-800 rounded-xl p-3">
          <div className="text-[12px] font-mono uppercase tracking-wider text-zinc-300">WP-Strom</div>
          <div className="text-base font-bold mt-1">{fmtKwh(data.hp_electricity_kwh)}</div>
          <div className="text-[12px] text-zinc-300">COP {data.prices_used?.hp_cop_used}</div>
        </div>
        <div className="bg-[#0E0E12] border border-zinc-800 rounded-xl p-3">
          <div className="text-[12px] font-mono uppercase tracking-wider text-zinc-300">Heizkosten WP</div>
          <div className="text-base font-bold mt-1 text-amber-300">{fmtEur(data.hp_cost_grid_eur)}/Jahr</div>
          <div className="text-[12px] text-zinc-300">ohne PV</div>
        </div>
        <div className="bg-[#DCFCE7]/10 border border-[#00E575]/40 rounded-xl p-3">
          <div className="text-[12px] font-mono uppercase tracking-wider text-[#00E575]">Heizkosten mit PV</div>
          <div className="text-base font-bold mt-1 text-[#00E575]">{fmtEur(data.hp_cost_with_pv_eur)}/Jahr</div>
          <div className="text-[12px] text-zinc-300">{pv.autarky_months}</div>
        </div>
      </div>

      {/* PV Recommendation Box */}
      <div className="bg-gradient-to-br from-[#00E575]/15 to-[#00E575]/5 border border-[#00E575]/40 rounded-2xl p-5">
        <div className="flex items-center gap-2 text-[#00E575] mb-3">
          <Zap size={16} /> <span className="text-[12px] font-bold uppercase tracking-wider">PV-Vorschlag</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <div className="text-2xl font-bold text-zinc-100">{pv.recommended_kwp} kWp</div>
            <div className="text-xs text-zinc-300 font-medium">Empf. Anlagengröße</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-zinc-100">{fmtEur(pv.investment_eur)}</div>
            <div className="text-xs text-zinc-300 font-medium">Investition</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-[#00E575]">{fmtEur(pv.annual_savings_eur)}</div>
            <div className="text-xs text-zinc-300 font-medium">Ersparnis pro Jahr</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-zinc-100">{pv.payback_years ? `${pv.payback_years} J.` : "—"}</div>
            <div className="text-xs text-zinc-300 font-medium">Amortisation</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="bg-[#09090B]/40 border border-zinc-800/60 rounded-xl p-3">
            <div className="flex items-center gap-1.5 text-[12px] uppercase tracking-wider text-zinc-400 mb-1">
              <Home size={12} className="text-zinc-500" /> Eigenverbrauch
            </div>
            <div className="font-mono text-sm font-semibold text-zinc-100">{fmtKwh(pv.self_consumption_kwh)}</div>
          </div>
          <div className="bg-[#09090B]/40 border border-zinc-800/60 rounded-xl p-3">
            <div className="flex items-center gap-1.5 text-[12px] uppercase tracking-wider text-zinc-400 mb-1">
              <Sun size={12} className="text-amber-400" /> Einspeisung
            </div>
            <div className="font-mono text-sm font-semibold text-zinc-100">{fmtKwh(pv.feed_in_kwh)}</div>
            <div className="text-[12px] text-zinc-300 mt-0.5">Ertrag: {fmtEur(pv.feed_in_income_eur)}/J.</div>
          </div>
        </div>
      </div>

      {/* Dämmung-Vergleich */}
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-300 mb-2 flex items-center gap-1.5">
          <TrendingDown size={12} /> Vergleich: Klassische Dämmungsmaßnahmen
        </div>
        <div className="space-y-2">
          {insul.map((opt) => {
            const [loRaw, hiRaw] = opt.payback_years_range || [];
            const lo = loRaw != null ? Math.floor(loRaw) : null;
            const hi = hiRaw != null ? Math.floor(hiRaw) : null;
            const slow = (lo || 0) > Math.floor((pv.payback_years || 0) * 2);
            const sameYear = lo === hi || hi == null;
            return (
              <div
                key={opt.kind}
                data-testid={`insulation-${opt.kind}`}
                className="bg-[#0E0E12] border border-zinc-800 rounded-xl p-4"
              >
                {/* Zeile 1: Maßnahme + Ersparnis-Anteil */}
                <div className="mb-3">
                  <div className="text-base font-semibold leading-tight">
                    {KIND_LABEL[opt.kind] || opt.kind}
                  </div>
                  <div className="text-[12px] text-zinc-300 mt-0.5">
                    ca. {opt.savings_pct} % der Heizkosten einsparbar
                  </div>
                </div>

                {/* Zeile 2: drei Kennzahlen untereinander für bessere Lesbarkeit */}
                <div className="space-y-2 text-sm">
                  <div className="bg-[#09090B]/60 border border-zinc-800 rounded-lg px-3 py-2.5 flex items-center justify-between gap-3">
                    <div className="text-[12px] uppercase tracking-wider text-zinc-300">Investition</div>
                    <div className="font-mono text-zinc-100 text-right">
                      {fmtEur(opt.cost_range_eur[0])} – {fmtEur(opt.cost_range_eur[1])}
                    </div>
                  </div>
                  <div className="bg-[#09090B]/60 border border-zinc-800 rounded-lg px-3 py-2.5 flex items-center justify-between gap-3">
                    <div className="text-[12px] uppercase tracking-wider text-zinc-300">Ersparnis / Jahr</div>
                    <div className="font-mono text-zinc-100 text-right">{fmtEur(opt.annual_savings_eur)}</div>
                  </div>
                  <div className={`rounded-lg px-3 py-2.5 flex items-center justify-between gap-3 ${
                    slow ? "bg-red-500/10 border border-red-500/30" : "bg-amber-500/10 border border-amber-500/30"
                  }`}>
                    <div className={`text-[12px] uppercase tracking-wider ${slow ? "text-red-400/80" : "text-amber-400/80"}`}>
                      Amortisation
                    </div>
                    <div className={`font-mono font-bold text-right ${slow ? "text-red-300" : "text-amber-300"}`}>
                      {sameYear ? `${lo} J.` : `${lo}–${hi} J.`}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Verdict – einklappbar */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl overflow-hidden" data-testid="verdict-card">
        <button
          type="button"
          onClick={() => setVerdictOpen((v) => !v)}
          aria-expanded={verdictOpen}
          data-testid="verdict-toggle"
          className="w-full flex items-center gap-3 p-4 text-left hover:bg-amber-500/15 transition"
        >
          <Leaf size={16} className="text-amber-300 flex-shrink-0" />
          <span className="text-sm font-bold uppercase tracking-wider text-amber-300 flex-1">
            Verkaufs-Argument
          </span>
          <ChevronDown
            size={18}
            className={`text-amber-300/70 transition-transform ${verdictOpen ? "rotate-180" : ""}`}
          />
        </button>
        {verdictOpen && (
          <div className="border-t border-amber-500/20 p-4">
            <p className="text-sm text-zinc-100 leading-relaxed">{data.verdict}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-[12px] text-zinc-300 leading-relaxed border-t border-zinc-800 pt-3">
        <b>Annahmen:</b> Heizöl 0,11 €/kWh · WP-Strom 0,28 €/kWh · Einspeisung 8,2 ct/kWh ·
        COP 4,0 · PV-Ertrag 950 kWh/kWp/J · Eigenverbrauch ~65 % · PV-Kosten 1.500 €/kWp.
        Werte sind Schätzungen, individuelle Standortfaktoren ändern das Ergebnis.
      </div>
    </div>
  );
}
